import { PickerRequest, SorCodeScope } from '@jms/model';
import type { ItemBrowserResult } from '@workbuddy/ui/blocks/line.items';
import type { ItemBrowserAddAdapter } from '@workbuddy/entities/shared/item.browser.dialog';
import type { JobItemAddService } from '../../../services/job.item.add.service';
import type { JobItemBoardParams } from '../../../models/job.item.model';

/**
 * JobItem's half of the shared catalogue create dialog.
 *
 * Two axes, both from the mount rather than the dialog: `sorOnly` (the SOR tab restricts the
 * catalogue to Schedule-of-Rates codes and relabels everything) and the Add Pending seed (writes to
 * the pending array instead of the authorised one). `onAdded` hands the board the new lines to light.
 */
export function buildJobItemBrowserAdapter(params: JobItemBoardParams, addService: JobItemAddService, initialValues?: Record<string, unknown>, onAdded?: (lineIds: string[]) => void): ItemBrowserAddAdapter {

  const sorOnly = params.sorOnly?.() === true;
  const pending = initialValues?.['pending'] === true;

  return {
    title: sorOnly
      ? (pending ? 'Browse Pending SOR' : 'Browse SOR')
      : (pending ? 'Browse Pending Items' : 'Browse Items'),
    codeHeader: sorOnly ? 'SOR ID' : 'Item',
    // Non-SOR mounts send nothing and inherit the server's SOR-excluded default.
    pickerRequest: sorOnly
      ? { filters: [{ _id: 'sorScope', value: SorCodeScope.Only }] } as PickerRequest
      : {},
    // Read live: the writable stage is resolved at submit time, not when the dialog opened.
    canAdd: () => !!params.job?.()?._id && !!params.stageId?.(),
    add: async (items: readonly ItemBrowserResult[]) => {
      const jobId = params.job()._id;
      const stageId = params.stageId();
      if (!jobId || !stageId) return;
      const outcome = await addService.add({ jobId, stageId, pending, items });
      // The lines written, not the ones picked: kits and bundles expand on the server. Empty still
      // calls back, so an add that wrote nothing clears the previous add's highlight.
      const lineIds = (outcome.results ?? []).filter(result => result.success && result._id).map(result => result._id!);
      onAdded?.(lineIds);
    },
  };
}
